import {
  ColumnFiltersState,
  ColumnPinningState,
  ColumnSizingState,
  ExpandedState,
  PaginationState,
  RowSelectionState,
  SortingState,
  Updater,
  VisibilityState,
} from '@tanstack/react-table';
import { atom, useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import {
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import {
  fetchColumnOrder,
  fetchColumnSizes,
  fetchTableCustomization,
  fetchColumnVisibility,
  fetchIsColumnFilterActive,
  updateColumnOrder,
  updateColumnPinning,
  updateColumnSizes,
  updateColumnVisibility,
  updateIsColumnFilterActive,
} from 'utils/tables';
import { buildInitialState, tableReducer } from './reducer';
import {
  ControllableState,
  DataTableSelectionState,
  TablePersistenceDefaults,
  TableState,
  UseTablePersistenceProps,
  UseTablePersistenceReturn,
} from './types';
import {
  RESERVED_COLUMN_IDS,
  RESERVED_COLUMN_SIZE,
  resolveUpdater,
} from './utils';
import { useContentWidths } from './content-widths';

// ─────────────────────────────────────
// Storage Key Suffixes
// ─────────────────────────────────────

const STORAGE_SUFFIXES = {
  visibility: '-visibility',
  order: '-order',
  pinning: '-pinning',
  sizing: '-sizing',
  columnFilterActive: '-column-filter-active',
} as const;

// ─────────────────────────────────────
// Column-order reconcile
// ─────────────────────────────────────

/**
 * A column's id as TanStack resolves it: the explicit `id`, else the `accessorKey` (when
 * it's a string), else `''`. Mirrors TanStack's own `id ?? accessorKey` so the wrapper's
 * derived order uses REAL ids — accessorKey-only columns no longer collapse to `''`.
 *
 * @param {object} col - A column def, read for its `id` / `accessorKey`.
 * @return {string} The resolved column id.
 */
export function columnId(col: {
  id?: string;
  accessorKey?: PropertyKey;
}): string {
  if (col.id !== undefined) return col.id;
  return typeof col.accessorKey === 'string' ? col.accessorKey : '';
}

/**
 * Reconciles a stored/seeded column order against the live column set. TanStack's own
 * ordering appends any column NOT in `columnOrder` to the END; this instead keeps the
 * base order for surviving columns, DROPS columns no longer present, and inserts each new
 * column at its COLDEF-relative position (right after its nearest preceding coldef
 * neighbor that survived — or the front if none). This is what lets a table with dynamic
 * columns behave like rows (a `columns`-prop change updates in place, no remount) while a
 * user's drag / a consumer's seed order is preserved for the columns they arranged.
 *
 * NOTE: `utils/tables.ts` has older twins (`insertColumnsAtCorrectPosition` / `mergeColumnOrder`)
 * still used by the legacy `lib/hooks/useColumnState.ts`. This is the single-pass replacement
 * for the DataTable wrapper — migrate consumers here rather than extending those.
 *
 * Pure + idempotent (StrictMode-safe): `reconcile(reconcile(x)) === reconcile(x)`.
 *
 * @param {string[]} base - The stored/seeded/controlled order (the arrangement to keep).
 * @param {string[]} coldefOrder - The live column ids in `columns`-prop order (incl. reserved prefixes).
 * @return {string[]} The reconciled order to feed TanStack.
 */
export function reconcileColumnOrder(
  base: string[],
  coldefOrder: string[]
): string[] {
  // `base` may not be an array — a missing / malformed persisted order, or a test that mocks
  // the backing atom, can hand us `undefined`. Raw TanStack tolerated a non-array
  // `columnOrder` (treated it as "no order" → definition order); mirror that instead of
  // throwing on `.filter`, so a bad stored value degrades to the coldef order, never a crash.
  const safeBase = Array.isArray(base) ? base : [];
  const coldefSet = new Set(coldefOrder);
  // Keep surviving base ids in their base order; drop ids no longer in the coldef.
  const result = safeBase.filter((id) => coldefSet.has(id));
  const present = new Set(result);
  // Insert each new coldef id (in coldef order) after its nearest preceding coldef
  // neighbour that is already placed — so new columns land at their prop position, and
  // consecutive new columns stay in prop order (each anchors off the previous).
  coldefOrder.forEach((id, coldefIndex) => {
    if (present.has(id)) return;
    let insertAt = 0;
    for (let i = coldefIndex - 1; i >= 0; i -= 1) {
      const pos = result.indexOf(coldefOrder[i]);
      if (pos !== -1) {
        insertAt = pos + 1;
        break;
      }
    }
    result.splice(insertAt, 0, id);
    present.add(id);
  });
  return result;
}

// ─────────────────────────────────────
// useTablePersistence Hook
// ─────────────────────────────────────

/**
 * Where a table persisting under `persistenceKey` keeps its column visibility.
 *
 * @param {string} persistenceKey - The table's `persistenceKey`.
 * @return {string} The localStorage key.
 */
const visibilityStorageKey = (persistenceKey: string): string =>
  `${persistenceKey}${STORAGE_SUFFIXES.visibility}`;

/**
 * The column visibility a table persisting under `persistenceKey` opens with: `defaults`, with
 * what the user saved over them — the merge `useTablePersistence` restores.
 *
 * @param {string} persistenceKey - The table's `persistenceKey`.
 * @param {VisibilityState} defaults - Its `initialTableState.columnVisibility`.
 * @return {VisibilityState} The visibility the table opens with.
 */
function persistedColumnVisibility(
  persistenceKey: string,
  defaults: VisibilityState
): VisibilityState {
  return {
    ...defaults,
    ...fetchColumnVisibility(visibilityStorageKey(persistenceKey)),
  };
}

/**
 * Hook that manages table customization state with optional localStorage persistence.
 * When persistenceKey is provided, state is synced to localStorage using atomWithStorage.
 * When not provided, state is kept in memory only.
 * @return {UseTablePersistenceReturn} Table persistence state and setters
 */
export function useTablePersistence({
  persistenceKey,
  defaults,
}: UseTablePersistenceProps): UseTablePersistenceReturn {
  // Build storage keys (null when not persisting); the visibility atom builds its own
  const orderKey = persistenceKey
    ? `${persistenceKey}${STORAGE_SUFFIXES.order}`
    : null;
  const pinningKey = persistenceKey
    ? `${persistenceKey}${STORAGE_SUFFIXES.pinning}`
    : null;
  const sizingKey = persistenceKey
    ? `${persistenceKey}${STORAGE_SUFFIXES.sizing}`
    : null;
  const filterActiveKey = persistenceKey
    ? `${persistenceKey}${STORAGE_SUFFIXES.columnFilterActive}`
    : null;

  // ─── Column Visibility Atom ───
  const visibilityAtom = useMemo(() => {
    if (persistenceKey) {
      return atomWithStorage<VisibilityState>(
        visibilityStorageKey(persistenceKey),
        defaults.columnVisibility,
        {
          // Defaults for columns not yet persisted (e.g. newly added hidden ones), saved state over them.
          getItem: () =>
            persistedColumnVisibility(
              persistenceKey,
              defaults.columnVisibility
            ),
          setItem: (key, val) => updateColumnVisibility(key, val),
          removeItem: () => void 0,
        },
        { getOnInit: true }
      );
    }
    return atom(defaults.columnVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persistenceKey]);

  // ─── Column Order Atom ───
  const orderAtom = useMemo(() => {
    if (orderKey) {
      return atomWithStorage<string[]>(
        orderKey,
        defaults.columnOrder,
        {
          // Reconcile the persisted order against the current columns: drop removed, keep
          // saved order, insert new columns at their coldef position (same rule as the
          // runtime derive) — replaces the old "prepend missing to the front" merge.
          getItem: (key) =>
            reconcileColumnOrder(fetchColumnOrder(key), defaults.columnOrder),
          setItem: (key, val) => updateColumnOrder(key, val),
          removeItem: () => void 0,
        },
        { getOnInit: true }
      );
    }
    return atom(defaults.columnOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderKey]);

  // ─── Column Pinning Atom ───
  const pinningAtom = useMemo(() => {
    if (pinningKey) {
      return atomWithStorage<ColumnPinningState>(
        pinningKey,
        defaults.columnPinning,
        {
          getItem: (key) => {
            const customization = fetchTableCustomization(key);
            return customization?.columnPinning !== undefined
              ? customization.columnPinning
              : defaults.columnPinning;
          },
          setItem: (key, val) => updateColumnPinning(key, val),
          removeItem: () => void 0,
        },
        { getOnInit: true }
      );
    }
    return atom(defaults.columnPinning);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinningKey]);

  // ─── Column Sizing Atom ───
  const sizingAtom = useMemo(() => {
    if (sizingKey) {
      return atomWithStorage<ColumnSizingState>(
        sizingKey,
        defaults.columnSizing,
        {
          getItem: (key) => {
            const stored = fetchColumnSizes(key);
            return Object.keys(stored).length > 0
              ? stored
              : defaults.columnSizing;
          },
          setItem: (key, val) => updateColumnSizes(key, val),
          removeItem: () => void 0,
        },
        { getOnInit: true }
      );
    }
    return atom(defaults.columnSizing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizingKey]);

  // ─── Column Filter Active Atom ───
  const filterActiveAtom = useMemo(() => {
    if (filterActiveKey) {
      return atomWithStorage<boolean>(
        filterActiveKey,
        false,
        {
          getItem: (key) => fetchIsColumnFilterActive(key),
          setItem: (key, val) => updateIsColumnFilterActive(key, val),
          removeItem: () => void 0,
        },
        { getOnInit: true }
      );
    }
    return atom(false);
  }, [filterActiveKey]);

  // ─── Use Atoms ───
  const [columnVisibility, setColumnVisibilityRaw] = useAtom(visibilityAtom);
  const [columnOrder, setColumnOrderRaw] = useAtom(orderAtom);
  const [columnPinning, setColumnPinningRaw] = useAtom(pinningAtom);
  const [columnSizing, setColumnSizingRaw] = useAtom(sizingAtom);
  const [isColumnFilterActive, setIsColumnFilterActive] =
    useAtom(filterActiveAtom);

  // ─── Updater Wrappers ───
  const setColumnVisibility = useCallback(
    (updaterOrValue: Updater<VisibilityState>) => {
      setColumnVisibilityRaw((prev) => resolveUpdater(updaterOrValue, prev));
    },
    [setColumnVisibilityRaw]
  );

  const setColumnOrder = useCallback(
    (updaterOrValue: Updater<string[]>) => {
      setColumnOrderRaw((prev) => resolveUpdater(updaterOrValue, prev));
    },
    [setColumnOrderRaw]
  );

  const setColumnPinning = useCallback(
    (updaterOrValue: Updater<ColumnPinningState>) => {
      setColumnPinningRaw((prev) => resolveUpdater(updaterOrValue, prev));
    },
    [setColumnPinningRaw]
  );

  const setColumnSizing = useCallback(
    (updaterOrValue: Updater<ColumnSizingState>) => {
      setColumnSizingRaw((prev) => resolveUpdater(updaterOrValue, prev));
    },
    [setColumnSizingRaw]
  );

  // ─── Reset Function ───
  const resetCustomizations = useCallback(() => {
    setColumnVisibilityRaw(defaults.columnVisibility);
    setColumnOrderRaw(defaults.columnOrder);
    setColumnPinningRaw(defaults.columnPinning);
    setColumnSizingRaw(defaults.columnSizing);
    setIsColumnFilterActive(false);

    // Clear every persisted slice from localStorage when persisting. Iterating
    // STORAGE_SUFFIXES keeps the suffix set defined in exactly one place.
    if (persistenceKey) {
      Object.values(STORAGE_SUFFIXES).forEach((suffix) =>
        localStorage.removeItem(`${persistenceKey}${suffix}`)
      );
    }
  }, [
    persistenceKey,
    defaults,
    setColumnVisibilityRaw,
    setColumnOrderRaw,
    setColumnPinningRaw,
    setColumnSizingRaw,
    setIsColumnFilterActive,
  ]);

  return useMemo(
    () => ({
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
      isColumnFilterActive,
      setColumnVisibility,
      setColumnOrder,
      setColumnPinning,
      setColumnSizing,
      setIsColumnFilterActive,
      resetCustomizations,
    }),
    [
      columnVisibility,
      columnOrder,
      columnPinning,
      columnSizing,
      isColumnFilterActive,
      setColumnVisibility,
      setColumnOrder,
      setColumnPinning,
      setColumnSizing,
      setIsColumnFilterActive,
      resetCustomizations,
    ]
  );
}

// ─────────────────────────────────────
// useTableState Hook Props
// ─────────────────────────────────────

interface UseTableStateProps {
  // Persistence
  persistenceKey?: string;
  /** Raw user columns. The hook injects `_select` / `_expander` IDs into
   *  the default column order/sizing internally when the flags below are set.
   *  `accessorKey` is read as the id fallback (TanStack resolves a column's id as
   *  `id ?? accessorKey`), so accessorKey-only columns get their real id, not `''`. */
  columns: Array<{ id?: string; accessorKey?: PropertyKey; size?: number }>;
  /** When true, `_select` is prepended to default column order + sizing. */
  enableCheckboxSelection?: boolean;
  /** When true, `_expander` is prepended to default column order + sizing. */
  hasExpansion?: boolean;
  /** Whether columns are drag-reorderable (default true). With no `initialColumnOrder`
   *  seed AND this false, `columnOrder` is "auto" (follows the live `columns` prop);
   *  otherwise it's "custom" (stored/seeded order preserved, reconciled against columns). */
  columnReordering?: boolean;

  // Column state
  initialColumnVisibility?: VisibilityState;
  initialColumnOrder?: string[];
  initialColumnPinning?: ColumnPinningState;

  // Filters
  initialColumnFilters?: ColumnFiltersState;
  initialGlobalFilter?: string;

  // Sorting
  initialSortColumn?: string;
  initialSortDirection?: 'asc' | 'desc';

  // Pagination
  initialPage?: number;
  initialPageSize?: number;

  // Expansion
  initialExpanded?: ExpandedState;

  // Server-mode flag — when true, wrapped setters skip TanStack-side
  // filtering/sorting/pagination and observers fire with snapshots assuming
  // the caller will refetch.
  isServerDriven?: boolean;

  // Selection
  initialSelection?: DataTableSelectionState;

  // Controlled-state overlay (opt-in, per slice). A key present here ⇒ the table reads
  // that slice from the prop instead of its reducer copy. @see ControllableState
  controlledState?: Partial<ControllableState>;

  totalCountProp?: number;

  // Per-event callbacks — each fires with a full `TableState` snapshot.
  // Phase-6 design; see `feature/data-table-refactor/06-per-event-callback-api.md`.
  // `onLoadMore` is NOT here — it fires from the infiniteScroll trigger in
  // `TableBody` and is wired by `data-table.tsx` directly (uses `buildSnapshot`).
  onPaginationChange?: (state: TableState) => void;
  onSortChange?: (state: TableState) => void;
  onFilterChange?: (state: TableState) => void;
  onGlobalSearchChange?: (state: TableState) => void;
  onSelectionChange?: (state: TableState) => void;
  onExpansionChange?: (state: TableState) => void;

  // Layout-slice callbacks — fire with the slice's NEW VALUE (not a snapshot), in both
  // controlled + uncontrolled modes.
  onColumnOrderChange?: (columnOrder: string[]) => void;
  onColumnPinningChange?: (columnPinning: ColumnPinningState) => void;
  onColumnVisibilityChange?: (columnVisibility: VisibilityState) => void;
  onColumnSizingChange?: (columnSizing: ColumnSizingState) => void;
}

// ─────────────────────────────────────
// useTableState Hook Return Type
// ─────────────────────────────────────

interface UseTableStateReturn {
  // Column visibility
  columnVisibility: VisibilityState;
  setColumnVisibility: (updaterOrValue: Updater<VisibilityState>) => void;

  // Column order
  columnOrder: string[];
  setColumnOrder: (updaterOrValue: Updater<string[]>) => void;

  // Column pinning
  columnPinning: ColumnPinningState;
  setColumnPinning: (updaterOrValue: Updater<ColumnPinningState>) => void;

  // Column sizing
  columnSizing: ColumnSizingState;
  setColumnSizing: (updaterOrValue: Updater<ColumnSizingState>) => void;

  // Filters — wrapped setters reset page atomically (reducer rule) and fire
  // the matching per-event callback with the full TableState snapshot.
  globalFilter: string;
  setGlobalFilter: (updaterOrValue: Updater<string>) => void;
  columnFilters: ColumnFiltersState;
  setColumnFilters: (updaterOrValue: Updater<ColumnFiltersState>) => void;

  // Sorting — wrapped setter resets page (reducer), fires onSortChange snapshot.
  sorting: SortingState;
  setSorting: (updaterOrValue: Updater<SortingState>) => void;

  // Pagination — raw setPagination is for TanStack's state-sync wiring;
  // handlePaginationChange is for Carbon <Pagination> onChange (fires onPaginationChange).
  pagination: PaginationState;
  setPagination: (updaterOrValue: Updater<PaginationState>) => void;
  handlePaginationChange: (state: { page: number; pageSize: number }) => void;

  // Expansion — wrapped setter fires onExpansionChange.
  expanded: ExpandedState;
  handleExpandedChange: (
    updaterOrValue: ExpandedState | ((old: ExpandedState) => ExpandedState)
  ) => void;

  // Selection — canonical AG-Grid shape (selectAll + toggledNodes). `rowSelection`
  // is derived and wired only to TanStack's `state.rowSelection` so `row.getIsSelected()`
  // works for table-body row highlighting in client mode. Server mode `rowSelection` = {}.
  // The selection column reads these via DataTableContext (in components extracted to
  // `selection-column.tsx`), so no ref escape hatch is needed.
  rowSelection: RowSelectionState;
  selectAll: boolean;
  setSelectAll: (value: boolean) => void;
  toggledNodes: Set<string>;
  setToggledNodes: (
    updaterOrValue: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
  /** Count of currently-selected rows. Server mode with selectAll=true is
   *  (totalCountProp ?? 0) - toggledNodes.size; otherwise toggledNodes.size. */
  selectionCount: number;
  /**
   * Called from selection-column components with the canonical AG-Grid pair
   * (selectAll + exceptions Set). Builds the snapshot internally and fires
   * `onSelectionChange`.
   */
  fireSelectionChange: (
    isAllSelected: boolean,
    exceptions: Set<string>
  ) => void;
  /** Build a snapshot from the current reducer state, with optional per-slice overrides. */
  buildSnapshot: (overrides?: Partial<TableState>) => TableState;

  // Filter panel state
  isFilterPanelOpen: boolean;
  setIsFilterPanelOpen: (open: boolean) => void;

  // Column filter active
  isColumnFilterActive: boolean;
  setIsColumnFilterActive: (active: boolean) => void;

  // Reset functions
  resetAllFilters: () => void;
  resetAllCustomizations: () => void;
}

// ─────────────────────────────────────
// useTableState Hook Implementation
// ─────────────────────────────────────

/**
 * Resolve a controlled/uncontrolled state slice: returns the consumer's `controlled`
 * value when provided (`!== undefined`), else the internal `fallback`. Control is decided
 * by the value's presence ALONE — the matching change callback is a separate notification,
 * so wiring only the callback never freezes the slice (a deliberate divergence from
 * TanStack). In dev, warns if a slice flips between controlled and uncontrolled across
 * renders (a React anti-pattern; mirrors React Aria's `useControlledState` + React's own
 * controlled-input warning).
 *
 * @param {T | undefined} controlled - The consumer-provided controlled value, or undefined.
 * @param {T} fallback - The internal (uncontrolled) value.
 * @param {string} name - Slice name, for the dev warning.
 * @return {T} The controlled value when provided, else the fallback.
 */
function useControlled<T>(
  controlled: T | undefined,
  fallback: T,
  name: string
): T {
  const isControlled = controlled !== undefined;
  // Dev-only controlled↔uncontrolled flip warning. Done in an effect (not during render)
  // so it never mutates a ref mid-render — that pattern is unsafe under StrictMode's
  // double-invoked render (the very issue this controlled API was hardened against).
  const wasControlled = useRef(isControlled);
  useEffect(() => {
    if (
      process.env.NODE_ENV !== 'production' &&
      wasControlled.current !== isControlled
    ) {
      console.error(
        `DataTable: \`${name}\` is switching between controlled and uncontrolled. ` +
          'Decide for the component lifetime — either always pass the value ' +
          '(controlled) or never pass it (uncontrolled, seeded by initialTableState).'
      );
    }
    wasControlled.current = isControlled;
  }, [isControlled, name]);
  // `controlled !== undefined` (not the `isControlled` var) so TS narrows `controlled` to T.
  return controlled !== undefined ? controlled : fallback;
}

/**
 * Owns all DataTable state: persisted column state (visibility, order, pinning,
 * sizing) via `useTablePersistence` + non-persisted state (filters, sorting,
 * pagination, expansion, selection, UI toggles).
 *
 * Wrapped setters dispatch atomic reducer actions (cross-slice rules like
 * "filter change resets page" baked into the reducer) and fire the matching
 * per-event callback with the full `TableState` snapshot.
 *
 * @param {UseTableStateProps} props - Configuration options
 * @return {UseTableStateReturn} Object containing all table state and setters
 */
export function useTableState({
  persistenceKey,
  columns,
  enableCheckboxSelection = false,
  hasExpansion = false,
  columnReordering = true,
  initialColumnVisibility = {},
  initialColumnOrder = [],
  initialColumnPinning = { left: [] },
  initialColumnFilters = [],
  initialGlobalFilter = '',
  initialSortColumn,
  initialSortDirection = 'asc',
  initialPage = 1,
  initialPageSize = DEFAULT_PAGE_SIZE,
  initialExpanded,
  isServerDriven = false,
  initialSelection,
  controlledState,
  totalCountProp,
  onPaginationChange,
  onSortChange,
  onFilterChange,
  onGlobalSearchChange,
  onSelectionChange,
  onExpansionChange,
  onColumnOrderChange,
  onColumnPinningChange,
  onColumnVisibilityChange,
  onColumnSizingChange,
}: UseTableStateProps): UseTableStateReturn {
  // ─── Derive Defaults from Column Definitions ───
  // Prefix the auto-injected `_select` / `_expander` columns into the
  // default order + sizing internally so callers can pass their raw
  // `columns` array without re-implementing the prefix logic upstream.
  const defaults = useMemo<TablePersistenceDefaults>(() => {
    const prefixIds: string[] = [];
    const prefixSizing: ColumnSizingState = {};
    if (enableCheckboxSelection) {
      prefixIds.push(RESERVED_COLUMN_IDS.SELECT);
      prefixSizing[RESERVED_COLUMN_IDS.SELECT] = RESERVED_COLUMN_SIZE;
    }
    if (hasExpansion) {
      prefixIds.push(RESERVED_COLUMN_IDS.EXPANDER);
      prefixSizing[RESERVED_COLUMN_IDS.EXPANDER] = RESERVED_COLUMN_SIZE;
    }

    const userIds = columns.map(columnId);
    const userSizing: ColumnSizingState = {};
    columns.forEach((col) => {
      if (col.size) {
        userSizing[columnId(col)] = col.size;
      }
    });

    return {
      columnVisibility: initialColumnVisibility,
      columnOrder:
        initialColumnOrder.length > 0
          ? initialColumnOrder
          : [...prefixIds, ...userIds],
      columnPinning: initialColumnPinning,
      columnSizing: { ...prefixSizing, ...userSizing },
    };
  }, [
    columns,
    enableCheckboxSelection,
    hasExpansion,
    initialColumnVisibility,
    initialColumnOrder,
    initialColumnPinning,
  ]);

  // ─── Persisted Column State (via useTablePersistence) ───
  const persistence = useTablePersistence({
    persistenceKey,
    defaults,
  });

  // ─── Applied column order (pure-derived reconcile) ───
  // The raw column ids in `columns`-prop order (reserved prefixes first) — the reference
  // the reconcile inserts NEW columns against, so they land at their prop position (not
  // last). Distinct from `defaults.columnOrder`, which is the `initialColumnOrder` SEED
  // when one is provided.
  const coldefOrder = useMemo(() => {
    const prefixIds: string[] = [];
    if (enableCheckboxSelection) prefixIds.push(RESERVED_COLUMN_IDS.SELECT);
    if (hasExpansion) prefixIds.push(RESERVED_COLUMN_IDS.EXPANDER);
    return [...prefixIds, ...columns.map(columnId)];
  }, [columns, enableCheckboxSelection, hasExpansion]);

  // "Custom" order = the columns were deliberately arranged (drag-reorderable OR an
  // `initialColumnOrder` seed) → keep the stored/seeded order, reconciled against the live
  // columns (drop removed, new columns at coldef position, existing-col coldef moves NOT
  // adopted). "Auto" (reordering off AND no seed) → the order just follows the `columns`
  // prop, so add/remove/move are all adopted. Pure-derived (no atom write) → StrictMode-safe
  // and no remount needed when `columns` changes. Fed to TanStack in place of the raw atom.
  const isCustomOrder =
    columnReordering !== false || initialColumnOrder.length > 0;
  // Base order = controlled prop ?? persisted atom, then reconciled against the live
  // columns. `columnOrder` (the applied value) is fed to TanStack + returned.
  const columnOrderBase = useControlled(
    controlledState?.columnOrder,
    persistence.columnOrder,
    'columnOrder'
  );
  const columnOrder = useMemo(() => {
    if (!isCustomOrder) return coldefOrder;
    return reconcileColumnOrder(columnOrderBase, coldefOrder);
  }, [isCustomOrder, columnOrderBase, coldefOrder]);

  // The other three layout slices — resolved controlled prop ?? persisted atom (no
  // reconcile: id-keyed maps tolerate a changing column set natively).
  const columnVisibility = useControlled(
    controlledState?.columnVisibility,
    persistence.columnVisibility,
    'columnVisibility'
  );
  const columnPinning = useControlled(
    controlledState?.columnPinning,
    persistence.columnPinning,
    'columnPinning'
  );
  const columnSizing = useControlled(
    controlledState?.columnSizing,
    persistence.columnSizing,
    'columnSizing'
  );

  // Layout setters: resolve the updater against the RESOLVED value (what TanStack sees),
  // write the persisted atom, and fire the consumer callback — a notification in BOTH
  // modes (a controlled consumer writes the value back; the atom write is then shadowed by
  // the controlled prop at read, matching the query-slice convention). The raw persistence
  // setters are destructured to stable locals so these callbacks memoize cleanly.
  const {
    setColumnOrder: persistSetColumnOrder,
    setColumnVisibility: persistSetColumnVisibility,
    setColumnPinning: persistSetColumnPinning,
    setColumnSizing: persistSetColumnSizing,
  } = persistence;
  const setColumnOrder = useCallback(
    (updaterOrValue: Updater<string[]>) => {
      const next = resolveUpdater(updaterOrValue, columnOrder);
      persistSetColumnOrder(next);
      onColumnOrderChange?.(next);
    },
    [columnOrder, persistSetColumnOrder, onColumnOrderChange]
  );
  const setColumnVisibility = useCallback(
    (updaterOrValue: Updater<VisibilityState>) => {
      const next = resolveUpdater(updaterOrValue, columnVisibility);
      persistSetColumnVisibility(next);
      onColumnVisibilityChange?.(next);
    },
    [columnVisibility, persistSetColumnVisibility, onColumnVisibilityChange]
  );
  const setColumnPinning = useCallback(
    (updaterOrValue: Updater<ColumnPinningState>) => {
      const next = resolveUpdater(updaterOrValue, columnPinning);
      persistSetColumnPinning(next);
      onColumnPinningChange?.(next);
    },
    [columnPinning, persistSetColumnPinning, onColumnPinningChange]
  );
  const setColumnSizing = useCallback(
    (updaterOrValue: Updater<ColumnSizingState>) => {
      const next = resolveUpdater(updaterOrValue, columnSizing);
      persistSetColumnSizing(next);
      onColumnSizingChange?.(next);
    },
    [columnSizing, persistSetColumnSizing, onColumnSizingChange]
  );

  // ─── Transient State Reducer ───
  // One reducer for all transient (non-persisted) state: filters, sort,
  // pagination, expansion, selection, UI. Cross-slice rules
  // (filter/sort/search reset page; RESET_ALL_FILTERS clears both filters +
  // resets page) are atomic dispatches in the reducer — no risk of
  // mid-flight observable inconsistency from sequential setStates.
  //
  // Persistence stays as `useTablePersistence` (atom-backed) — see
  // `feature/data-table-refactor/00-roadmap.md` for the architectural commitment.
  const [state, dispatch] = useReducer(
    tableReducer,
    {
      initialGlobalFilter,
      initialColumnFilters,
      initialSortColumn,
      initialSortDirection,
      initialPage,
      initialPageSize,
      initialExpanded,
      initialSelection,
    },
    buildInitialState
  );

  // ─── Controlled-state overlay (opt-in, per slice) ───
  // The resolved value of a slice = the consumer's controlled prop (when provided) else the
  // reducer's copy. The WRITE path is unchanged: dispatching to a controlled slice is
  // harmless because nothing below reads the reducer's copy of it (the parent applies the
  // real change from the `on*Change` snapshot). Cross-slice resets (filter/sort → page 1)
  // stay atomic in the reducer for the uncontrolled case; when pagination is controlled the
  // reducer's reset is dead and the snapshot's `page: 1` tells the parent to reset.
  // ADDING A SLICE: adapt its public→internal shape (as below) + one `useControlled` call,
  // then read the resolved value in `buildSnapshot`, the TanStack `state` block, and below.
  const controlledPage = controlledState?.pagination;
  // Adapt the public 1-based `{ page, pageSize }` to TanStack's 0-based PaginationState.
  // Memoized on the PRIMITIVE page/pageSize (not the object identity) so an inline
  // `controlledState={{ pagination: {...} }}` from the parent doesn't churn the resolved
  // value every render — deps are intentionally the scalars, not `controlledPage`. The
  // destructure makes the field set explicit: adding a field to `pagination` forces a
  // visible edit here (and its dep) rather than silently going stale under the disable.
  const { page: controlledPageNum, pageSize: controlledPageSize } =
    controlledPage ?? {};
  const controlledPagination = useMemo<PaginationState | undefined>(() => {
    if (controlledPageNum === undefined || controlledPageSize === undefined) {
      return undefined;
    }
    return { pageIndex: controlledPageNum - 1, pageSize: controlledPageSize };
  }, [controlledPageNum, controlledPageSize]);
  const pagination = useControlled(
    controlledPagination,
    state.pagination,
    'pagination'
  );

  // `sorting` + `globalFilter` need no public→internal adaptation (the public shape IS
  // the internal shape), so they resolve directly. Omitting the slice ⇒ uncontrolled
  // (reducer-backed), exactly as before. Read the resolved value in `buildSnapshot`, the
  // wrapped setters, the TanStack `state` block, and the return (via `sorting` /
  // `globalFilter` below) so a controlled value shows immediately.
  const globalFilter = useControlled(
    controlledState?.globalFilter,
    state.globalFilter,
    'globalFilter'
  );
  const sorting = useControlled(
    controlledState?.sorting,
    state.sorting,
    'sorting'
  );
  const columnFilters = useControlled(
    controlledState?.columnFilters,
    state.columnFilters,
    'columnFilters'
  );
  const expanded = useControlled(
    controlledState?.expanded,
    state.expanded,
    'expanded'
  );

  // Selection needs a public→internal adaptation (public `toggledNodes` is a `string[]`,
  // the reducer holds a `Set`). Like the `controlledPagination` adapter above, the rebuild
  // is keyed on CONTENT, not object identity — but the content is an array, so it uses a
  // stringified signature instead of primitive deps. The ids are SORTED first so the memo
  // treats `toggledNodes` as the unordered set it represents: an equal-but-reordered
  // selection is NOT a change, so it won't needlessly rebuild the `Set` (which would churn
  // `rowSelection` / the context). This also absorbs an inline
  // `controlledState={{ selection: {...} }}` without churning every render.
  const controlledSelection = controlledState?.selection;
  const controlledSelectionSignature = controlledSelection
    ? JSON.stringify([
        controlledSelection.selectAll,
        [...controlledSelection.toggledNodes].sort(),
      ])
    : undefined;
  const controlledInternalSelection = useMemo<
    { selectAll: boolean; toggledNodes: Set<string> } | undefined
  >(() => {
    if (!controlledSelection) return undefined;
    return {
      selectAll: controlledSelection.selectAll,
      toggledNodes: new Set(controlledSelection.toggledNodes),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledSelectionSignature]);
  const selection = useControlled(
    controlledInternalSelection,
    state.selection,
    'selection'
  );

  // ─── Selection Derivations ───
  // Live count of currently-selected rows (for maxSelections limit enforcement).
  const selectionCount = isServerDriven
    ? selection.selectAll
      ? (totalCountProp ?? 0) - selection.toggledNodes.size
      : selection.toggledNodes.size
    : selection.toggledNodes.size;

  // Derived TanStack rowSelection — wired to `state.rowSelection` so
  // `row.getIsSelected()` works for table-body row highlight in client mode.
  // Returns {} in server mode (TanStack doesn't need to know — visual highlight
  // for server mode is a pre-existing UX gap, orthogonal to this hook) AND
  // when selectAll=true (we'd need all data row IDs to enumerate exceptions
  // into a TanStack record, and the hook doesn't have data access).
  const rowSelection = useMemo<RowSelectionState>(() => {
    if (isServerDriven || selection.selectAll) return {};
    return Object.fromEntries(
      [...selection.toggledNodes].map((id) => [id, true])
    );
  }, [selection.selectAll, selection.toggledNodes, isServerDriven]);

  // `initialSelection` is a MOUNT-ONLY seed (consumed by `buildInitialState` above).
  // External/reactive selection control now flows through `controlledState.selection`
  // (resolved by `useControlled` above); the old reactive re-sync effect was removed once
  // every reactive-seed consumer migrated to the controlled slice.

  // ─── Snapshot helper ───
  // Build a full TableState snapshot from current reducer state + optional
  // per-slice overrides. Wrapped setters pass overrides for the slice that
  // just changed (because React state hasn't committed at callback-fire time)
  // and for `page` when the reducer auto-resets it. Slice-level deps (not
  // `state`) keep memoization tight — `state` identity changes per action.
  const buildSnapshot = useCallback(
    (overrides: Partial<TableState> = {}): TableState => ({
      // Read the RESOLVED pagination (controlled prop ?? reducer) so snapshots are correct
      // in both modes — every other callback's `page`/`pageSize` reflects what's displayed.
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sorting,
      columnFilters,
      globalFilter,
      selection: {
        selectAll: selection.selectAll,
        toggledNodes: [...selection.toggledNodes],
      },
      expanded,
      ...overrides,
    }),
    [pagination, sorting, columnFilters, globalFilter, selection, expanded]
  );

  // ─── Wrapped Setters ────────────────────────────────────────────
  // Each setter: resolve updater → dispatch atomic reducer action → fire
  // matching per-event callback with snapshot (override the slice that
  // changed + `page: 1` if the reducer auto-reset it).
  const setColumnFilters = useCallback(
    (updaterOrValue: Updater<ColumnFiltersState>) => {
      const newFilters = resolveUpdater(updaterOrValue, state.columnFilters);
      dispatch({ type: 'SET_COLUMN_FILTERS', payload: newFilters });
      onFilterChange?.(buildSnapshot({ columnFilters: newFilters, page: 1 }));
    },
    [state.columnFilters, buildSnapshot, onFilterChange]
  );

  const setGlobalFilter = useCallback(
    (updaterOrValue: Updater<string>) => {
      // Resolve against the RESOLVED value (controlled prop ?? reducer) so a functional
      // updater computes from the displayed base in both modes (mirrors setPagination).
      const newText = resolveUpdater(updaterOrValue, globalFilter);
      dispatch({ type: 'SET_GLOBAL_FILTER', payload: newText });
      onGlobalSearchChange?.(buildSnapshot({ globalFilter: newText, page: 1 }));
    },
    [globalFilter, buildSnapshot, onGlobalSearchChange]
  );

  const setSorting = useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      const newSorting = resolveUpdater(updaterOrValue, sorting);
      dispatch({ type: 'SET_SORTING', payload: newSorting });
      onSortChange?.(buildSnapshot({ sorting: newSorting, page: 1 }));
    },
    [sorting, buildSnapshot, onSortChange]
  );

  // For TanStack's `onPaginationChange` wiring — accepts Updater<PaginationState>.
  // No observer call; pure state-sync (Carbon's `<Pagination>` onChange goes
  // through `handlePaginationChange` below, which DOES fire `onPaginationChange`).
  const setPagination = useCallback(
    (updaterOrValue: Updater<PaginationState>) => {
      // Resolve against the displayed (resolved) pagination — that's the state TanStack
      // was given, so a functional updater computes from the right base in both modes.
      const next = resolveUpdater(updaterOrValue, pagination);
      dispatch({ type: 'SET_PAGINATION', payload: next });
    },
    [pagination]
  );

  // Carbon <Pagination> onChange receives 1-based page. No page reset — the
  // click IS the change.
  const handlePaginationChange = useCallback(
    ({ page, pageSize }: { page: number; pageSize: number }) => {
      dispatch({
        type: 'SET_PAGINATION',
        payload: { pageIndex: page - 1, pageSize },
      });
      onPaginationChange?.(buildSnapshot({ page, pageSize }));
    },
    [buildSnapshot, onPaginationChange]
  );

  const handleExpandedChange = useCallback(
    (
      updaterOrValue: ExpandedState | ((old: ExpandedState) => ExpandedState)
    ) => {
      // Resolve against the RESOLVED value (controlled prop ?? reducer), as setSorting does, so a
      // row's expand toggle computes from what the table shows in both modes.
      const newExpanded =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(expanded)
          : updaterOrValue;
      dispatch({ type: 'SET_EXPANDED', payload: newExpanded });
      onExpansionChange?.(buildSnapshot({ expanded: newExpanded }));
    },
    [expanded, buildSnapshot, onExpansionChange]
  );

  // ─── Selection setters (used by selection-column components via context) ───
  const setSelectAll = useCallback(
    (value: boolean) => {
      dispatch({
        type: 'SET_SELECTION',
        payload: {
          selectAll: value,
          toggledNodes: selection.toggledNodes,
        },
      });
    },
    [selection.toggledNodes]
  );

  const setToggledNodes = useCallback(
    (updaterOrValue: Set<string> | ((prev: Set<string>) => Set<string>)) => {
      const newToggled =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(selection.toggledNodes)
          : updaterOrValue;
      dispatch({
        type: 'SET_SELECTION',
        payload: {
          selectAll: selection.selectAll,
          toggledNodes: newToggled,
        },
      });
    },
    [selection.selectAll, selection.toggledNodes]
  );

  // Called from selection-column components when user toggles a checkbox.
  // Builds the snapshot internally and fires `onSelectionChange`.
  const fireSelectionChange = useCallback(
    (isAllSelected: boolean, exceptions: Set<string>) => {
      onSelectionChange?.(
        buildSnapshot({
          selection: {
            selectAll: isAllSelected,
            toggledNodes: [...exceptions],
          },
        })
      );
    },
    [buildSnapshot, onSelectionChange]
  );

  const setIsFilterPanelOpen = useCallback((open: boolean) => {
    dispatch({ type: 'SET_FILTER_PANEL_OPEN', payload: open });
  }, []);

  // ─── Multi-slice resets ───
  // Atomic via reducer: RESET_ALL_FILTERS clears both filters + resets page
  // in one dispatch. Both onFilterChange + onGlobalSearchChange fire with the
  // SAME snapshot (no intermediate state observable mid-flight).
  const resetAllFilters = useCallback(() => {
    dispatch({ type: 'RESET_ALL_FILTERS' });
    const snapshot = buildSnapshot({
      globalFilter: '',
      columnFilters: [],
      page: 1,
    });
    onGlobalSearchChange?.(snapshot);
    onFilterChange?.(snapshot);
  }, [buildSnapshot, onFilterChange, onGlobalSearchChange]);

  // "Reset to default" (the toolbar button) reverts EVERYTHING — layout, sort, AND filters.
  // It fires each affected callback ONCE with a single, fully-reset snapshot. Building one
  // consistent snapshot here (instead of pairing this with a separate resetAllFilters(), each
  // producing a partial snapshot) is what prevents a consumer that funnels every callback into
  // one param-setter from ending up with a MIX of reset + stale slices (e.g. reset filters but
  // stale sort — the order the two used to fire in). resetAllFilters stays separate for the
  // filters-only "Clear filters" affordances (ResetFilter button, FilterPanel).
  const resetAllCustomizations = useCallback(() => {
    // Layout (atom-backed) → defaults, notifying per slice. The RESET INTENT: an uncontrolled
    // consumer already sees the atom reset (this is a heads-up), while a CONTROLLED consumer
    // (whose value shadows the atom) applies it by writing back — the only way the table can
    // reset a slice it doesn't own. Fires in both modes; also closes the pre-existing gap
    // where a controlled slice's reset was a silent, shadowed atom write.
    persistence.resetCustomizations();
    onColumnOrderChange?.(defaults.columnOrder);
    onColumnVisibilityChange?.(defaults.columnVisibility);
    onColumnPinningChange?.(defaults.columnPinning);
    onColumnSizingChange?.(defaults.columnSizing);

    // Sort → initial, filters/search → empty, page → 1. Dispatch the transient resets for
    // uncontrolled consumers, then notify controlled/server consumers with ONE snapshot so
    // every callback carries the same fully-reset state regardless of which one they act on.
    const resetSorting: SortingState = initialSortColumn
      ? [{ id: initialSortColumn, desc: initialSortDirection === 'desc' }]
      : [];
    dispatch({ type: 'SET_SORTING', payload: resetSorting });
    dispatch({ type: 'RESET_ALL_FILTERS' });
    const snapshot = buildSnapshot({
      sorting: resetSorting,
      globalFilter: '',
      columnFilters: [],
      page: 1,
    });
    onSortChange?.(snapshot);
    onGlobalSearchChange?.(snapshot);
    onFilterChange?.(snapshot);
  }, [
    persistence,
    defaults,
    buildSnapshot,
    onSortChange,
    onGlobalSearchChange,
    onFilterChange,
    initialSortColumn,
    initialSortDirection,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onColumnPinningChange,
    onColumnSizingChange,
  ]);

  return useMemo(
    () => ({
      // Column state — RESOLVED values (controlled prop ?? persisted atom) + callback-firing
      // setters. `columnOrder` is additionally reconciled against the live columns, so a
      // `columns`-prop change reflects in place with no remount.
      columnVisibility,
      setColumnVisibility,
      columnOrder,
      setColumnOrder,
      columnPinning,
      setColumnPinning,
      columnSizing,
      setColumnSizing,
      // Filters — the RESOLVED globalFilter (controlled prop ?? reducer), fed to
      // TanStack's `state` block + the search input so a controlled value shows at once.
      globalFilter,
      setGlobalFilter,
      // The RESOLVED columnFilters (controlled prop ?? reducer).
      columnFilters,
      setColumnFilters,
      // Sorting — the RESOLVED value (controlled prop ?? reducer).
      sorting,
      setSorting,
      // Pagination — the RESOLVED value (controlled prop ?? reducer). Fed to TanStack's
      // `state` block + Carbon `<Pagination>`, so a controlled page shows immediately.
      pagination,
      setPagination,
      handlePaginationChange,
      // Expansion — the RESOLVED value (controlled prop ?? reducer).
      expanded,
      handleExpandedChange,
      // Selection
      rowSelection,
      selectAll: selection.selectAll,
      setSelectAll,
      toggledNodes: selection.toggledNodes,
      setToggledNodes,
      selectionCount,
      fireSelectionChange,
      buildSnapshot,
      // UI state
      isFilterPanelOpen: state.isFilterPanelOpen,
      setIsFilterPanelOpen,
      isColumnFilterActive: persistence.isColumnFilterActive,
      setIsColumnFilterActive: persistence.setIsColumnFilterActive,
      // Reset functions
      resetAllFilters,
      resetAllCustomizations,
    }),
    [
      persistence,
      columnVisibility,
      setColumnVisibility,
      columnOrder,
      setColumnOrder,
      columnPinning,
      setColumnPinning,
      columnSizing,
      setColumnSizing,
      globalFilter,
      setGlobalFilter,
      columnFilters,
      setColumnFilters,
      sorting,
      setSorting,
      pagination,
      setPagination,
      handlePaginationChange,
      expanded,
      handleExpandedChange,
      rowSelection,
      selection.selectAll,
      setSelectAll,
      selection.toggledNodes,
      setToggledNodes,
      selectionCount,
      fireSelectionChange,
      buildSnapshot,
      state.isFilterPanelOpen,
      setIsFilterPanelOpen,
      resetAllFilters,
      resetAllCustomizations,
    ]
  );
}

// ─────────────────────────────────────
// useColumnWidths Hook
// ─────────────────────────────────────

interface UseColumnWidthsProps {
  containerRef: RefObject<HTMLElement>;
  columns: Array<{
    id?: string;
    accessorKey?: PropertyKey;
    size?: number;
    meta?: { fixedWidth?: boolean };
  }>;
  defaultColumnWidth?: number;
  /** When true, use exact column sizes without distributing extra container width */
  fitContent?: boolean;
  /** Size each column to its widest rendered header or cell; wrap columns share the space left. */
  sizeToContent?: boolean;
  /** Changes when the rendered rows change, so content sizing re-measures. */
  contentKey?: unknown;
  wrapColumnIds?: string[];
  isLoading?: boolean;
}

interface UseColumnWidthsReturn {
  effectiveWidths: Record<string, number>;
  updateColumnWidth: (colId: string, width: number) => void;
  resetWidths: () => void;
}

/**
 * Hook that manages column widths with initial distribution.
 * On first render, distributes extra space equally among columns.
 * After that, resize updates only affect the resized column.
 * @return {UseColumnWidthsReturn} Object with effectiveWidths, updateColumnWidth, and resetWidths
 */
export function useColumnWidths({
  containerRef,
  columns,
  defaultColumnWidth = 150,
  fitContent = false,
  sizeToContent = false,
  contentKey,
  wrapColumnIds,
  isLoading,
}: UseColumnWidthsProps): UseColumnWidthsReturn {
  const [effectiveWidths, setEffectiveWidths] = useState<
    Record<string, number>
  >({});
  const resizedRef = useRef(new Set<string>());
  // Signature of the current column SET. When it changes (e.g. a Group-by swap on a table
  // that no longer remounts), widths must be reconciled against the new set — otherwise they
  // stay keyed to the OLD columns and a subsequent resize desyncs header vs body. A resize
  // keeps the same signature, so it does NOT trigger reconciliation.
  const columnSignature = useMemo(
    () => columns.map((col) => columnId(col)).join('|'),
    [columns]
  );
  const distributedSignatureRef = useRef<string | null>(null);

  // Calculate base widths from column definitions
  const baseWidths = useMemo(() => {
    const widths: Record<string, number> = {};
    columns.forEach((col) => {
      const id = columnId(col);
      if (id) {
        widths[id] = col.size ?? defaultColumnWidth;
      }
    });
    return widths;
  }, [columns, defaultColumnWidth]);

  const columnIds = useMemo(
    () => columns.map((col) => columnId(col)).filter(Boolean),
    [columns]
  );
  // Opt-in `meta.fixedWidth` columns keep their size, like the reserved select/expander ones.
  const fixedWidthIds = useMemo(
    () =>
      columns.filter((col) => col.meta?.fixedWidth).map((col) => columnId(col)),
    [columns]
  );

  // First paint distributes the extra container width across every column so the table fills
  // its width. On a later column-set change (a Group-by regroup — this table no longer
  // remounts) we do NOT reflow: every SURVIVING column keeps its current width — its initial
  // share OR a user resize, treated identically — and only the freed space is handed to
  // brand-new columns, with removed columns dropped. One uniform rule (no "some columns
  // pinned, others reflow"), so a resize sticks across a regroup. The per-signature guard
  // makes same-column-set re-renders (e.g. a resize) O(1).
  useLayoutEffect(() => {
    if (!containerRef.current || isLoading) return;
    if (distributedSignatureRef.current === columnSignature) return;

    const isInitial = distributedSignatureRef.current === null;
    const containerWidth = containerRef.current.clientWidth;
    const fixedIds = new Set<string>([
      RESERVED_COLUMN_IDS.EXPANDER,
      RESERVED_COLUMN_IDS.SELECT,
      ...fixedWidthIds,
    ]);
    const baseOf = (id: string) => baseWidths[id] ?? defaultColumnWidth;

    setEffectiveWidths((prev) => {
      // Columns needing a fresh width: all on first paint, only newly-added ones afterwards.
      const isNew = (id: string) => isInitial || prev[id] === undefined;
      // Width already committed to the surviving columns we're preserving.
      const preserved = columnIds
        .filter((id) => !isNew(id))
        .reduce((sum, id) => sum + prev[id], 0);
      // Spread the remaining space across the new NON-fixed columns (on first paint that's
      // all of them → original fill behavior; on a regroup it's just the added columns, so
      // survivors never reflow yet the table still fills).
      const newIds = columnIds.filter(isNew);
      const newBaseTotal = newIds.reduce((sum, id) => sum + baseOf(id), 0);
      const flexNewCount = newIds.filter((id) => !fixedIds.has(id)).length;
      const free = containerWidth - preserved - newBaseTotal;
      // Content sizing owns the final widths; `size` is only the pre-measure width.
      const extra =
        !fitContent && !sizeToContent && free > 0 && flexNewCount > 0
          ? free / flexNewCount
          : 0;

      const next: Record<string, number> = {};
      columnIds.forEach((id) => {
        if (!isNew(id)) next[id] = prev[id];
        else next[id] = fixedIds.has(id) ? baseOf(id) : baseOf(id) + extra;
      });
      return next;
    });
    // A dropped column's resize goes with its width, so content sizing measures it on return.
    resizedRef.current.forEach((id) => {
      if (!columnIds.includes(id)) resizedRef.current.delete(id);
    });

    distributedSignatureRef.current = columnSignature;
  }, [
    containerRef,
    columnIds,
    fixedWidthIds,
    baseWidths,
    defaultColumnWidth,
    fitContent,
    sizeToContent,
    isLoading,
    columnSignature,
  ]);

  const resetContentWidths = useContentWidths({
    containerRef,
    columnIds,
    wrapColumnIds,
    fixedColumnIds: fixedWidthIds,
    contentKey,
    enabled: sizeToContent && !isLoading,
    resizedIds: resizedRef,
    setWidths: setEffectiveWidths,
  });

  // Handle resize - update single column only. The new width lives in `effectiveWidths`, so
  // it's automatically preserved as a "survivor" width across a later column-set change.
  const updateColumnWidth = useCallback((colId: string, width: number) => {
    resizedRef.current.add(colId);
    setEffectiveWidths((prev) => ({ ...prev, [colId]: width }));
  }, []);

  // Reset to recalculate distribution — clearing the signature makes the next run treat the
  // current columns as a first paint (full distribution again).
  const resetWidths = useCallback(() => {
    distributedSignatureRef.current = null;
    resizedRef.current.clear();
    setEffectiveWidths({});
    resetContentWidths();
  }, [resetContentWidths]);

  return { effectiveWidths, updateColumnWidth, resetWidths };
}
